import { CanMatchFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { catchError, map, of, switchMap, throwError } from 'rxjs';
import { defaultAuthConfiguration, logoutWithReturnUrl } from '@touch4it/ng-swp/auth';
import { CrmConfigService } from '@touch4it/ng-swp/core';
import { CrmLayoutMenuConfig } from '@touch4it/ng-swp/layout-v2';

import { UsersApiService } from '~/api/users/users-api.service';
import { ProfilesApiService } from '~/api/profiles/profiles-api.service';

export const appGuard: CanMatchFn = (_, segments) => {
  const userService = inject(UsersApiService);
  const profilesService = inject(ProfilesApiService);
  const router = inject(Router);
  const authConfig = inject(CrmConfigService).mergeConfig(defaultAuthConfiguration, 'modules.auth');
  const menuItemsConfig =
    inject(CrmConfigService)
      .getConfig<CrmLayoutMenuConfig>('modules.layoutV2.menu')
      ?.items?.map(({ routeKey }) => routeKey) ?? [];

  const firstSegmentPath = segments[0]?.path;
  const savedWorkspace = profilesService.getStoredWorkspace();

  if (firstSegmentPath && menuItemsConfig.includes(firstSegmentPath) && savedWorkspace) {
    return router.createUrlTree([savedWorkspace, ...segments.map(({ path }) => path)]);
  }

  return userService.user$.pipe(
    switchMap((user) => {
      if (user.roles.admin) {
        return profilesService.listAllWorkspaces().pipe(
          map((c) => {
            const found = c.find(({ namespace }) => namespace === firstSegmentPath);

            return found ?? c[0];
          }),
        );
      }

      if (!user.workspaceId) {
        return throwError(() => new Error(`no workspace ID for user '${user._id}'`));
      }

      return profilesService.getWorkspace(user.workspaceId);
    }),
    map(({ namespace }) => {
      if (namespace === firstSegmentPath) {
        return true;
      }

      return router.createUrlTree([namespace, ...segments.map(({ path }) => path)]);
    }),
    catchError(() => {
      logoutWithReturnUrl({ userService, router, authConfig });
      return of(false);
    }),
  );
};
